/**
 * The camera cutout is always a circle of a fixed size — one less decision to
 * make before recording, and it keeps every Bloom video looking consistent.
 * Its diameter is a fraction of the frame width, so it scales with resolution.
 */
export const CAM_SIZE = 0.18

/**
 * Where the camera cutout sits, in *normalised* coordinates so the position is
 * resolution-independent: x/y are 0..1 of the frame's width/height and describe
 * the CENTRE of the circle.
 */
export interface CamLayout {
  x: number
  y: number
  mirror: boolean
  hidden: boolean
}

export const DEFAULT_CAM_LAYOUT: CamLayout = {
  x: 0.86,
  y: 0.8,
  mirror: true,
  hidden: false,
}

/** A raw take straight out of the recorder. Immutable once recorded. */
export interface Source {
  id: string
  blob: Blob
  url: string
  /** Seconds. Measured by the recorder itself — WebM headers don't carry it. */
  duration: number
  width: number
  height: number
  recordedAt: number
}

/** A visible slice of a Source on the timeline. Editing only ever changes these. */
export interface Clip {
  id: string
  sourceId: string
  /** Seconds into the source where this slice starts. */
  in: number
  /** Seconds into the source where this slice ends. */
  out: number
}

export const clipLength = (c: Clip) => Math.max(0, c.out - c.in)

export const timelineLength = (clips: Clip[]) =>
  clips.reduce((total, c) => total + clipLength(c), 0)

/** Map a global timeline position to the clip that owns it. */
export function locate(clips: Clip[], t: number) {
  let acc = 0
  for (let i = 0; i < clips.length; i++) {
    const len = clipLength(clips[i])
    if (t < acc + len || i === clips.length - 1) {
      return { index: i, clip: clips[i], local: Math.min(len, Math.max(0, t - acc)), offset: acc }
    }
    acc += len
  }
  return null
}

/** Global timeline position where a clip begins. */
export function clipStart(clips: Clip[], index: number) {
  let acc = 0
  for (let i = 0; i < index; i++) acc += clipLength(clips[i])
  return acc
}

export const uid = () => Math.random().toString(36).slice(2, 10)

export function formatTime(seconds: number, withTenths = false) {
  if (!Number.isFinite(seconds) || seconds < 0) seconds = 0
  const total = Math.floor(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const tenths = Math.floor((seconds - total) * 10)
  const base =
    h > 0
      ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      : `${m}:${String(s).padStart(2, '0')}`
  return withTenths ? `${base}.${tenths}` : base
}
